import { findLsItems, getLsItem, setLsItem } from './localStorage'

type CacheEntry<T> = {
  createdAt: number
  value: T
}

type RequestFn<T, Args extends unknown[]> = (...args: Args) => Promise<T>

const DEFAULT_CACHE_DURATION_MS = 5 * 60 * 1000
const CACHE_REQUEST_LS_KEY = 'cacheRequest'

const buildCacheKey = (name: string, args: unknown[], version: number) => {
  return [CACHE_REQUEST_LS_KEY, name, version, JSON.stringify(args)].filter(Boolean).join(':')
}

export const clearExpiredCaches = () => {
  const caches = findLsItems<CacheEntry<unknown>>(CACHE_REQUEST_LS_KEY)
  const now = Date.now()

  Object.entries(caches).forEach(([key, cache]) => {
    if (!cache || !cache.createdAt || cache.createdAt + DEFAULT_CACHE_DURATION_MS <= now) {
      setLsItem(key)
    }
  })
}

const cacheRequest = async <T, Args extends unknown[]>(
  request: RequestFn<T, Args>,
  args: Args,
  name: string,
  version = 1,
) => {
  const cacheKey = buildCacheKey(name, args, version)
  const cache = getLsItem<CacheEntry<T>>(cacheKey)

  if (cache && cache.createdAt + DEFAULT_CACHE_DURATION_MS > Date.now()) {
    return cache.value
  }

  const response = await request(...args)

  setLsItem<CacheEntry<T>>(cacheKey, {
    createdAt: Date.now(),
    value: response,
  })

  return response
}

export default cacheRequest
